import { DataTable } from '@/components/table/DataTable';
import { Text } from '@/components/ui/text';
import { useTableViewsService } from '@/services/tableViews';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTableViewsColumns } from './columns';

interface TableViewsTableProps {
  searchString?: string;
}

export const TableViewsTable = ({
  searchString = '',
}: TableViewsTableProps) => {
  const columns = useTableViewsColumns();
  const { tableViews, isPending, error } = useTableViewsService();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Reset to first page when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [searchString]);

  const filteredTableViews = useMemo(() => {
    if (!searchString.trim()) {
      return tableViews || [];
    }
    const lowerSearch = searchString.toLowerCase().trim();
    return (tableViews || []).filter(tv =>
      tv.name.toLowerCase().includes(lowerSearch)
    );
  }, [tableViews, searchString]);

  if (error) {
    return (
      <Text variant="body-sm" color="primary">
        An error occurred
      </Text>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={filteredTableViews}
      isLoading={isPending}
      options={{
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        enableMultiSort: false,
        getRowId: row => row.id,
        autoResetPageIndex: false,
        state: {
          pagination,
          sorting,
        },
      }}
    />
  );
};
